import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import PhoneInput from "react-phone-number-input";

interface Ticket {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  quantity: number;
  status: string;
  price?: number;
  event?: {
    _id: string;
    name: string;
    tourId?: string;
    tourName?: string;
  };
}

interface Event {
  _id: string;
  name: string;
}

export function TicketEdit() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    event: "",
    quantity: "",
    status: "complete",
    price: "",
  });

  useEffect(() => {
    async function fetchTicket() {
      if (!ticketId) return;

      try {
        const [ticketResponse, eventsResponse] = await Promise.all([
          fetch(`/api/tickets/${ticketId}`),
          fetch(`/api/events`),
        ]);

        if (ticketResponse.ok) {
          const data = await ticketResponse.json();
          setTicket(data.ticket);

          setFormData({
            name: data.ticket.name || "",
            email: data.ticket.email || "",
            phoneNumber: data.ticket.phoneNumber || "",
            event: data.ticket.event?._id?.toString() || "",
            quantity: data.ticket.quantity?.toString() || "",
            status: data.ticket.status || "complete",
            price: data.ticket.price?.toString() || "",
          });

          // Set breadcrumbs
          const ticketName = data.ticket.name || data.ticket.email || "Ticket";
          const breadcrumbs = [
            { label: "Tickets", path: "/tickets" },
          ];
          
          // Add tour link if it exists
          if (data.ticket.event?.tourId && data.ticket.event?.tourName) {
            breadcrumbs.push({ 
              label: data.ticket.event.tourName, 
              path: `/tours/${data.ticket.event.tourId}` 
            });
          }
          
          // Add event link if it exists
          if (data.ticket.event?.name) {
            breadcrumbs.push({ 
              label: data.ticket.event.name, 
              path: data.ticket.event.tourId 
                ? `/tours/${data.ticket.event.tourId}/events/${data.ticket.event._id}/edit`
                : `/events/${data.ticket.event._id}/edit`
            });
          }
          
          breadcrumbs.push({ 
            label: ticketName, 
            path: `/tickets/${ticketId}/edit` 
          });
          setBreadcrumbs(breadcrumbs);
        } else {
          console.error("Failed to fetch ticket");
        }

        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          setEvents(eventsData.events || []);
        } else {
          console.error("Failed to fetch events");
        }
      } catch (error) {
        console.error("Failed to fetch ticket:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchTicket();

    return () => {
      clearBreadcrumbs();
    };
  }, [ticketId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketId) return;

    setIsSubmitting(true);
    try {
      const payload: any = {
        name: formData.name || undefined,
        email: formData.email,
        quantity: parseInt(formData.quantity, 10),
        status: formData.status,
      };

      if (formData.phoneNumber) {
        payload.phoneNumber = formData.phoneNumber;
      }
      if (formData.event) {
        payload.event = formData.event;
      }
      if (formData.price) {
        payload.price = parseFloat(formData.price);
      }

      const response = await fetch(`/api/tickets/${ticketId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate(`/tickets`);
      } else {
        const data = await response.json();
        console.error("Failed to update ticket:", data.error);
        alert(data.error || "Failed to update ticket");
      }
    } catch (error) {
      console.error("Failed to update ticket:", error);
      alert("An error occurred while updating the ticket");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!ticket) {
    return <div className="p-4">Ticket not found</div>;
  }

  return (
    <div className="p-4">
      <Card>
        <CardHeader>
          <CardTitle>Edit Ticket</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <PhoneInput
                id="phoneNumber"
                defaultCountry="US"
                international={false}
                value={formData.phoneNumber}
                onChange={(value) => setFormData((prev) => ({ ...prev, phoneNumber: value || "" }))}
                className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event">Event *</Label>
              <Select
                value={formData.event}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, event: value }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger id="event">
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map((event) => (
                    <SelectItem key={event._id} value={event._id}>
                      {event.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  name="quantity"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, status: value }))
                }
                disabled={isSubmitting}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="uncaptured">Uncaptured</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/tickets`)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

